import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

type Sleutel = readonly unknown[];

/**
 * Maakt een mutatie die na afloop de gegeven queries ververst, zodat lijsten en details weer kloppen.
 * @param uitvoeren De aanroep van de API.
 * @param verversen De querysleutels die na succes verlopen; krijgt de invoer en het resultaat mee.
 * @returns De mutatie.
 */
export function useApiMutatie<TInvoer, TResultaat>(
  uitvoeren: (invoer: TInvoer) => Promise<TResultaat>,
  verversen: (invoer: TInvoer, resultaat: TResultaat) => readonly Sleutel[],
): UseMutationResult<TResultaat, Error, TInvoer> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uitvoeren,
    onSuccess: async (resultaat, invoer) => {
      await Promise.all(verversen(invoer, resultaat).map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
  });
}

/**
 * Verwijdert lege waarden uit een filter, zodat alleen ingevulde parameters in de query komen.
 * @param filter Het filter met mogelijk lege waarden.
 * @returns Het filter zonder lege waarden.
 */
export function zonderLeeg<T extends Record<string, unknown>>(filter: T): { [K in keyof T]?: NonNullable<T[K]> } {
  const resultaat: Record<string, unknown> = {};
  for (const [sleutel, waarde] of Object.entries(filter)) {
    if (waarde !== undefined && waarde !== null && waarde !== '') {
      resultaat[sleutel] = waarde;
    }
  }
  return resultaat as { [K in keyof T]?: NonNullable<T[K]> };
}
